import React, { useMemo } from "react";
import type { MemberSearchHiddenKeywordMatch } from "../../../Services/MemberSearch";

type MemberSearchKeywordMatchHintProps = {
  match: MemberSearchHiddenKeywordMatch;
  keyword: string;
};

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const buildTriggerLabel = (fieldLabels: string[]): string => {
  if (fieldLabels.length === 1) {
    return `Matches in ${fieldLabels[0]}`;
  }

  return `Matches in ${fieldLabels.length} fields (${fieldLabels.join(", ")})`;
};

const MatchCheckIcon: React.FC = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
    className="mt-px shrink-0"
  >
    <path
      d="M2.25 6.25L4.75 8.75L9.75 3.75"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const KeywordHighlightedText: React.FC<{ text: string; keyword: string }> = ({
  text,
  keyword,
}) => {
  const parts = useMemo(() => {
    const normalizedKeyword = keyword.trim();
    if (!normalizedKeyword) {
      return [text];
    }

    const regex = new RegExp(`(${escapeRegExp(normalizedKeyword)})`, "gi");
    return text.split(regex).filter(Boolean);
  }, [keyword, text]);

  const normalizedKeyword = keyword.trim().toLowerCase();

  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === normalizedKeyword ? (
          <strong key={`${part}-${index}`}>{part}</strong>
        ) : (
          <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
        ),
      )}
    </>
  );
};

const MemberSearchKeywordMatchHint: React.FC<MemberSearchKeywordMatchHintProps> = ({
  match,
  keyword,
}) => (
  <div className="group/match relative w-full border-t border-dotted border-gray-300 pt-2">
    <p className="flex cursor-default items-start gap-1.5 text-[11px] leading-snug text-gray-font">
      <span className="text-green-600">
        <MatchCheckIcon />
      </span>
      <span className="group-hover/match:underline">
        {buildTriggerLabel(match.fieldLabels)}
      </span>
    </p>
    <div
      className="pointer-events-none absolute left-0 top-full z-50 mt-1 hidden max-w-sm rounded border border-gray-200 bg-white p-2 text-[11px] leading-snug text-neutral-dark shadow-lg group-hover/match:block"
      role="tooltip"
    >
      <KeywordHighlightedText text={match.excerpt} keyword={keyword} />
    </div>
  </div>
);

export default MemberSearchKeywordMatchHint;
